import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2 } from "lucide-react";

const CATS = ["ball_control", "passing", "first_touch", "technique", "shooting", "fitness"];

function EmptyRow({ children, cols }) {
    return (
        <TableRow>
            <TableCell colSpan={cols} className="text-center py-10 text-white/40 font-heading tracking-widest">
                {children}
            </TableCell>
        </TableRow>
    );
}

function ExerciseForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(
        initial || {
            title: "", category: "ball_control", difficulty: "beginner", duration_minutes: 10,
            description: "", instructions: [], video_url: "", thumbnail_url: "", tags: [],
        },
    );
    const [instructionsText, setInstructionsText] = useState((initial?.instructions || []).join("\n"));
    const [tagsText, setTagsText] = useState((initial?.tags || []).join(", "));
    const save = async () => {
        try {
            const payload = {
                ...form,
                duration_minutes: Number(form.duration_minutes),
                instructions: instructionsText.split("\n").map((s) => s.trim()).filter(Boolean),
                tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
            };
            if (initial?.id) await api.put(`/admin/exercises/${initial.id}`, payload);
            else await api.post(`/admin/exercises`, payload);
            toast.success("Saved");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };
    return (
        <div className="space-y-4">
            <div><Label>Title</Label><Input data-testid="ex-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div className="grid grid-cols-3 gap-3">
                <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="bg-black border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div><Label>Difficulty</Label>
                    <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                        <SelectTrigger className="bg-black border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div><Label>Instructions (one per line)</Label><Textarea rows={4} value={instructionsText} onChange={(e) => setInstructionsText(e.target.value)} className="bg-black border-white/10 rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>YouTube Embed URL</Label><Input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="bg-black border-white/10 rounded-none" /></div>
                <div><Label>Thumbnail URL</Label><Input value={form.thumbnail_url || ""} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            </div>
            <div><Label>Tags (comma-separated)</Label><Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="bg-black border-white/10 rounded-none" /></div>
            <DialogFooter>
                <button data-testid="ex-form-save" onClick={save} className="btn-volt px-6 py-2">Save</button>
            </DialogFooter>
        </div>
    );
}

function ProgramForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(
        initial || {
            title: "", subtitle: "", description: "", duration_weeks: 4, level: "all",
            goals: [], video_url: "", thumbnail_url: "", exercise_ids: [],
        },
    );
    const [goalsText, setGoalsText] = useState((initial?.goals || []).join("\n"));
    const save = async () => {
        try {
            const payload = {
                ...form,
                duration_weeks: Number(form.duration_weeks),
                goals: goalsText.split("\n").map((s) => s.trim()).filter(Boolean),
                exercise_ids: form.exercise_ids || [],
            };
            if (initial?.id) await api.put(`/admin/programs/${initial.id}`, payload);
            else await api.post(`/admin/programs`, payload);
            toast.success("Saved");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };
    return (
        <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Duration (weeks)</Label><Input type="number" value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
                <div><Label>Level</Label>
                    <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                        <SelectTrigger className="bg-black border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div><Label>Goals (one per line)</Label><Textarea rows={4} value={goalsText} onChange={(e) => setGoalsText(e.target.value)} className="bg-black border-white/10 rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>YouTube Embed URL</Label><Input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
                <div><Label>Thumbnail URL</Label><Input value={form.thumbnail_url || ""} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            </div>
            <DialogFooter>
                <button onClick={save} className="btn-volt px-6 py-2">Save</button>
            </DialogFooter>
        </div>
    );
}

function ReviewForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(
        initial || { student_name: "", club_name: "", photo_url: "", quote: "", achievement: "", rating: 5 },
    );
    const save = async () => {
        try {
            const payload = { ...form, rating: Number(form.rating) };
            if (initial?.id) await api.put(`/admin/reviews/${initial.id}`, payload);
            else await api.post(`/admin/reviews`, payload);
            toast.success("Saved");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Student Name</Label><Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
                <div><Label>Club / Academy</Label><Input value={form.club_name} onChange={(e) => setForm({ ...form, club_name: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            </div>
            <div><Label>Photo URL</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div><Label>Quote</Label><Textarea rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Achievement</Label><Input value={form.achievement} onChange={(e) => setForm({ ...form, achievement: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
                <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="bg-black border-white/10 rounded-none" /></div>
            </div>
            <DialogFooter>
                <button onClick={save} className="btn-volt px-6 py-2">Save</button>
            </DialogFooter>
        </div>
    );
}

function CrudDialog({ trigger, title, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="bg-zinc-950 border-white/10 rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">{title}</DialogTitle>
                </DialogHeader>
                {typeof children === "function" ? children({ onClose: () => setOpen(false) }) : children}
            </DialogContent>
        </Dialog>
    );
}

export default function AdminDashboardPage() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [exercises, setExercises] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [signups, setSignups] = useState([]);

    const refresh = () => {
        api.get("/exercises").then((r) => setExercises(r.data));
        api.get("/programs").then((r) => setPrograms(r.data));
        api.get("/reviews").then((r) => setReviews(r.data));
        api.get("/admin/signups").then((r) => setSignups(r.data)).catch(() => setSignups([]));
    };
    useEffect(() => { refresh(); }, []);

    const doLogout = async () => { await logout(); nav("/admin/login"); };

    const del = (kind, id) => async () => {
        if (!window.confirm("Delete this item?")) return;
        try {
            await api.delete(`/admin/${kind}/${id}`);
            toast.success("Deleted");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const updateSignupStatus = async (id, status) => {
        try {
            await api.put(`/admin/signups/${id}`, { status });
            toast.success("Updated");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-background">
            <header className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-xl z-40">
                <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 bg-volt inline-block" />
                    <span className="font-heading text-lg tracking-wider">EPA · Control Room</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-xs uppercase tracking-widest text-white/50">{user?.email}</span>
                    <button data-testid="admin-logout" onClick={doLogout} className="btn-ghost-outline px-4 py-2 text-xs inline-flex items-center gap-2">
                        <LogOut size={14} /> Log out
                    </button>
                </div>
            </header>

            <div className="px-6 md:px-12 py-10">
                <Tabs defaultValue="signups">
                    <TabsList className="bg-zinc-900 border border-white/10 rounded-none h-auto p-1">
                        <TabsTrigger data-testid="tab-signups" value="signups" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black font-heading tracking-widest text-xs px-5 py-2">Signups ({signups.length})</TabsTrigger>
                        <TabsTrigger data-testid="tab-exercises" value="exercises" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black font-heading tracking-widest text-xs px-5 py-2">Exercises ({exercises.length})</TabsTrigger>
                        <TabsTrigger data-testid="tab-programs" value="programs" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black font-heading tracking-widest text-xs px-5 py-2">Programs ({programs.length})</TabsTrigger>
                        <TabsTrigger data-testid="tab-reviews" value="reviews" className="rounded-none data-[state=active]:bg-volt data-[state=active]:text-black font-heading tracking-widest text-xs px-5 py-2">Reviews ({reviews.length})</TabsTrigger>
                    </TabsList>

                    {/* SIGNUPS */}
                    <TabsContent value="signups" className="mt-6">
                        <div className="border border-white/10 bg-zinc-900/40">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead>Name</TableHead>
                                        <TableHead>Contact</TableHead>
                                        <TableHead>Age</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Goals</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {signups.length === 0 ? <EmptyRow cols={7}>NO APPLICATIONS YET</EmptyRow> : signups.map((s) => (
                                        <TableRow key={s.id} data-testid={`signup-row-${s.id}`} className="border-white/10 hover:bg-zinc-900">
                                            <TableCell className="font-heading text-base">{s.full_name}</TableCell>
                                            <TableCell className="text-xs"><div>{s.email}</div><div className="text-white/50">{s.phone}</div></TableCell>
                                            <TableCell>{s.age}</TableCell>
                                            <TableCell><span className="text-xs uppercase tracking-widest text-volt">{s.training_type}</span></TableCell>
                                            <TableCell className="max-w-xs text-xs text-white/70 truncate">{s.goals}</TableCell>
                                            <TableCell>
                                                <Select value={s.status} onValueChange={(v) => updateSignupStatus(s.id, v)}>
                                                    <SelectTrigger className="bg-black border-white/10 rounded-none w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="new">New</SelectItem>
                                                        <SelectItem value="contacted">Contacted</SelectItem>
                                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                                        <SelectItem value="archived">Archived</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <button data-testid={`signup-delete-${s.id}`} onClick={del("signups", s.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    {/* EXERCISES */}
                    <TabsContent value="exercises" className="mt-6">
                        <div className="flex justify-end mb-4">
                            <CrudDialog
                                title="New Exercise"
                                trigger={<button data-testid="new-exercise-btn" className="btn-volt px-5 py-2 inline-flex items-center gap-2"><Plus size={14} /> New Exercise</button>}
                            >
                                {({ onClose }) => <ExerciseForm onClose={onClose} onSaved={refresh} />}
                            </CrudDialog>
                        </div>
                        <div className="border border-white/10 bg-zinc-900/40">
                            <Table>
                                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Difficulty</TableHead><TableHead>Duration</TableHead><TableHead>Actions</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {exercises.length === 0 ? <EmptyRow cols={5}>NO EXERCISES</EmptyRow> : exercises.map((e) => (
                                        <TableRow key={e.id} className="border-white/10 hover:bg-zinc-900">
                                            <TableCell className="font-heading">{e.title}</TableCell>
                                            <TableCell className="text-xs uppercase tracking-widest text-volt">{e.category.replace("_", " ")}</TableCell>
                                            <TableCell className="text-xs uppercase">{e.difficulty}</TableCell>
                                            <TableCell className="text-xs">{e.duration_minutes} min</TableCell>
                                            <TableCell className="flex items-center gap-3">
                                                <CrudDialog
                                                    title="Edit Exercise"
                                                    trigger={<button className="text-white/50 hover:text-volt"><Pencil size={14} /></button>}
                                                >
                                                    {({ onClose }) => <ExerciseForm initial={e} onClose={onClose} onSaved={refresh} />}
                                                </CrudDialog>
                                                <button onClick={del("exercises", e.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    {/* PROGRAMS */}
                    <TabsContent value="programs" className="mt-6">
                        <div className="flex justify-end mb-4">
                            <CrudDialog
                                title="New Program"
                                trigger={<button data-testid="new-program-btn" className="btn-volt px-5 py-2 inline-flex items-center gap-2"><Plus size={14} /> New Program</button>}
                            >
                                {({ onClose }) => <ProgramForm onClose={onClose} onSaved={refresh} />}
                            </CrudDialog>
                        </div>
                        <div className="border border-white/10 bg-zinc-900/40">
                            <Table>
                                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead>Title</TableHead><TableHead>Weeks</TableHead><TableHead>Level</TableHead><TableHead>Actions</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {programs.length === 0 ? <EmptyRow cols={4}>NO PROGRAMS</EmptyRow> : programs.map((p) => (
                                        <TableRow key={p.id} className="border-white/10 hover:bg-zinc-900">
                                            <TableCell><div className="font-heading">{p.title}</div><div className="text-xs text-white/50">{p.subtitle}</div></TableCell>
                                            <TableCell className="text-xs">{p.duration_weeks}</TableCell>
                                            <TableCell className="text-xs uppercase">{p.level}</TableCell>
                                            <TableCell className="flex items-center gap-3">
                                                <CrudDialog
                                                    title="Edit Program"
                                                    trigger={<button className="text-white/50 hover:text-volt"><Pencil size={14} /></button>}
                                                >
                                                    {({ onClose }) => <ProgramForm initial={p} onClose={onClose} onSaved={refresh} />}
                                                </CrudDialog>
                                                <button onClick={del("programs", p.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    {/* REVIEWS */}
                    <TabsContent value="reviews" className="mt-6">
                        <div className="flex justify-end mb-4">
                            <CrudDialog
                                title="New Review"
                                trigger={<button data-testid="new-review-btn" className="btn-volt px-5 py-2 inline-flex items-center gap-2"><Plus size={14} /> New Review</button>}
                            >
                                {({ onClose }) => <ReviewForm onClose={onClose} onSaved={refresh} />}
                            </CrudDialog>
                        </div>
                        <div className="border border-white/10 bg-zinc-900/40">
                            <Table>
                                <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead>Student</TableHead><TableHead>Club</TableHead><TableHead>Achievement</TableHead><TableHead>Rating</TableHead><TableHead>Actions</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {reviews.length === 0 ? <EmptyRow cols={5}>NO REVIEWS</EmptyRow> : reviews.map((r) => (
                                        <TableRow key={r.id} className="border-white/10 hover:bg-zinc-900">
                                            <TableCell className="font-heading">{r.student_name}</TableCell>
                                            <TableCell className="text-xs text-white/70">{r.club_name}</TableCell>
                                            <TableCell className="text-xs text-volt uppercase tracking-widest">{r.achievement}</TableCell>
                                            <TableCell className="text-xs">{r.rating}/5</TableCell>
                                            <TableCell className="flex items-center gap-3">
                                                <CrudDialog
                                                    title="Edit Review"
                                                    trigger={<button className="text-white/50 hover:text-volt"><Pencil size={14} /></button>}
                                                >
                                                    {({ onClose }) => <ReviewForm initial={r} onClose={onClose} onSaved={refresh} />}
                                                </CrudDialog>
                                                <button onClick={del("reviews", r.id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
